import React from 'react';
import Joyride, { Step, CallBackProps, STATUS } from 'react-joyride';
import { X } from 'lucide-react';
import type { TutorialProps } from '../../lib/utils/types';
import { useTutorialState } from '../../lib/store/hooks';

const steps: Step[] = [
  {
    target: '.ai-insights',
    content: 'Get instant AI-powered insights about your business finances',
    placement: 'bottom',
    title: 'AI Insights',
    disableBeacon: true,
  },
  {
    target: '.receipt-analyzer',
    content: 'Simply drag and drop your receipts for automatic analysis and categorization',
    placement: 'right',
    title: 'Receipt Analysis',
  },
  {
    target: '.contract-analyzer',
    content: 'Upload contracts to get AI-powered analysis of terms, risks, and recommendations',
    placement: 'left',
    title: 'Contract Analysis',
  },
  {
    target: '.support-assistant',
    content:
      'Ask any questions about accounting, taxes, or the software - our AI assistant is here to help',
    placement: 'right',
    title: 'AI Support',
  },
];

export function Tutorial({ onDismiss }: TutorialProps) {
  const [tutorialState, setTutorialState] = useTutorialState();

  const handleJoyrideCallback = React.useCallback((data: CallBackProps) => {
    const { action, index, status, type } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      onDismiss();
      setTutorialState(prev => ({ ...prev, isOpen: false }));
    } else if (type === 'step:after') {
      setTutorialState(prev => ({ ...prev, stepIndex: index + 1 }));
    }
  }, [onDismiss, setTutorialState]);

  return (
    <div className="relative" role="region" aria-label="Product tutorial">
      <button
        onClick={() => {
          onDismiss();
          setTutorialState(prev => ({ ...prev, isOpen: false }));
        }}
        className="absolute top-2 right-2 text-dark-muted hover:text-dark-text transition-colors p-1 rounded-full hover:bg-dark-bg focus:outline-none focus:ring-2 focus:ring-primary-400"
        aria-label="Dismiss tutorial"
      >
        <X className="h-5 w-5" aria-hidden="true" />
      </button>
      <Joyride
        steps={steps}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        stepIndex={tutorialState.stepIndex}
        run={tutorialState.isOpen}
        styles={{
          options: {
            primaryColor: '#0ea5e9',
            backgroundColor: '#1F2937',
            textColor: '#F9FAFB',
            arrowColor: '#1F2937',
            zIndex: 1000,
          },
          tooltip: {
            padding: '1rem',
          },
          buttonNext: {
            backgroundColor: '#0ea5e9',
          },
          buttonBack: {
            color: '#9CA3AF',
          },
        }}
      />
    </div>
  );
}